import React from "react"
import styled, { css } from "styled-components"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import { ContainerLayout, Intro } from "../components/common"
import variables from "../data/variables"

const TWO_COLUMN_ART = new Set(["art-047"])

const ART_TITLES = {
  "art-047": "A Fantasy Map",
}

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-flow: dense;
  gap: 1rem;

  @media (max-width: ${variables.breakpointLarge}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${variables.breakpointPhone}) {
    grid-template-columns: 1fr;
  }
`

const ArtImageWrap = styled.div`
  position: relative;
  width: 100%;

  ${({ $wide }) =>
    $wide &&
    css`
      width: calc((100% - 2rem) * 2 / 3 + 1rem);
      margin: 0 auto;

      @media (max-width: ${variables.breakpointLarge}) {
        width: 100%;
      }
    `}
`

const ArtLabel = styled.span`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 1;
  padding: 0.35rem 0.65rem;
  border-radius: 0;
  border: 2px solid ${variables.black};
  font-family: ${variables.pixelFont};
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  color: #232323;
  background: #fff;
`

const ArtTile = styled.figure`
  margin: 0;
  border-radius: 0;
  border: ${variables.pixelBorder} solid ${variables.black};
  overflow: hidden;
  box-shadow: ${variables.pixelShadow} ${variables.shadowColor};
  background: #f7f7f7;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 250ms ease;
    aspect-ratio: 1 / 1;
  }

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
      background: transparent;
      box-shadow: none;
      border: none;
      overflow: visible;

      img {
        width: 100%;
        margin: 0;
        height: auto;
        aspect-ratio: auto;
        object-fit: contain;
        border-radius: 0;
        border: ${variables.pixelBorder} solid ${variables.black};
        box-shadow: ${variables.pixelShadow} ${variables.shadowColor};
        background: #f7f7f7;
      }

      @media (max-width: ${variables.breakpointLarge}) {
        img {
          width: 100%;
        }
      }
    `}

  &:hover img {
    transform: scale(1.04);
  }
`

const ArtIntro = styled.p`
  margin-top: 0.25rem;
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #232323;
`

const ArtPage = ({ data }) => {
  const artImages = data.allFile.nodes
    .slice()
    .sort((a, b) => {
      const aNum = Number((a.name.match(/\d+/) || ["0"])[0])
      const bNum = Number((b.name.match(/\d+/) || ["0"])[0])
      return bNum - aNum
    })

  return (
    <Layout>
      <SEO title="Art" />
      <Intro>
        <ContainerLayout>
          <ArtIntro>
            I practice with fine-line ink and oil pastel mediums, exploring quiet everyday moments.
          </ArtIntro>
          <ArtGrid>
            {artImages.map((image, index) => {
              const wide = TWO_COLUMN_ART.has(image.name)
              const title = index === 0 ? ART_TITLES[image.name] : null

              return (
                <ArtTile key={image.id} $wide={wide}>
                  <ArtImageWrap $wide={wide}>
                    {title && <ArtLabel>{title}</ArtLabel>}
                    <img
                      src={image.publicURL}
                      alt={title || `Artwork ${index + 1}`}
                      loading="lazy"
                      draggable={false}
                    />
                  </ArtImageWrap>
                </ArtTile>
              )
            })}
          </ArtGrid>
        </ContainerLayout>
      </Intro>
    </Layout>
  )
}

export default ArtPage

export const pageQuery = graphql`
  query {
    allFile(
      filter: {
        sourceInstanceName: { eq: "art" }
        extension: { in: ["jpg", "jpeg", "png", "webp"] }
      }
    ) {
      nodes {
        id
        name
        publicURL
      }
    }
  }
`
